import React from 'react';
import { Button } from '@mui/material';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import { notify } from '../../notifications';
import { ACTION_BUTTON_SX } from '../reports/summary/summaryStyles';

interface BankReportProps {
  tableData: any[];
  selectedUser: { username: string; pharmacyNum: string } | null;
  startDate: string;
  endDate: string;
  employeeName: string | null;
  /** Принудительно отключить кнопку (например, пока данные за выбранный период перезагружаются) */
  disabled?: boolean;
  /** Вызывается после того, как файл сформирован (запись в журнал) */
  onGenerated?: () => void;
}

const BankReport: React.FC<BankReportProps> = ({ tableData, selectedUser, startDate, endDate, employeeName, disabled, onGenerated }) => {
  const getMonthName = (date: string): string => {
    const months = [
      'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
      'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
    ];
    const monthIndex = new Date(date).getMonth();
    return months[monthIndex];
  };

  const getYear = (date: string): string => {
    return new Date(date).getFullYear().toString();
  };

  const generateReport = async () => {
    try {
      const response = await fetch('/templates/bank_report_template.docx');
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      
      const rows = tableData.map((row, index) => {
        const getValue = (field: any) => {
          return field !== undefined && field !== null && field !== '' ? parseFloat(field) : 0;
        };
      
        const rowTotal = 
          getValue(row.narodny_bank) +
          getValue(row.bereke_bank) +
          getValue(row.kaspi_red_terminal) +
          getValue(row.delivery_service_kaspi_store) +
          getValue(row.glovo) +
          getValue(row.volt) +
          getValue(row.yandex);
      
        // const totalTotal = 
        //   getValue(row.narodny_bank) +
        //   getValue(row.bereke_bank) +
        //   getValue(row.kaspi_red_terminal) +
        //   getValue(row.delivery_service_kaspi_store) +
        //   getValue(row.glovo) +
        //   getValue(row.volt) +
        //   getValue(row.yandex);

        return {
          num: index + 1,
          date: row.date,
          narodny_bank: getValue(row.narodny_bank),
          bereke_bank: getValue(row.bereke_bank),
          kaspi_red_terminal: getValue(row.kaspi_red_terminal),
          delivery_service_kaspi_store: getValue(row.delivery_service_kaspi_store),
          glovo: getValue(row.glovo),
          volt: getValue(row.volt),
          yandex: getValue(row.yandex),
          totalrow: rowTotal,
          cashless:  getValue(row.cashless) || 0,
          // totalTotal: totalTotal,
          total_revenue_zreport: getValue(row.total_revenue_zreport),
        };
      });

      const totals = rows.reduce((acc, row) => ({
        totalnarodny_bank: acc.totalnarodny_bank + row.narodny_bank || 0,
        totalBereke_bank: acc.totalBereke_bank + row.bereke_bank || 0,
        totalkaspi_red_terminal: acc.totalkaspi_red_terminal + row.kaspi_red_terminal || 0,
        totaldelivery_service_kaspi_store: acc.totaldelivery_service_kaspi_store + row.delivery_service_kaspi_store || 0,
        totalglovo: acc.totalglovo + row.glovo || 0,
        totalvolt: acc.totalvolt + row.volt || 0,
        totalyandex: acc.totalyandex + row.yandex || 0,
        totalrow: acc.totalrow + (row.narodny_bank + row.bereke_bank + row.kaspi_red_terminal + row.delivery_service_kaspi_store + row.glovo + row.volt + row.yandex) || 0,
        totalzreport: acc.totalzreport + row.cashless || 0,
        totalTotal: ((acc.totalnarodny_bank + row.narodny_bank) + (acc.totalBereke_bank + row.bereke_bank) + (acc.totalkaspi_red_terminal + row.kaspi_red_terminal) + (acc.totaldelivery_service_kaspi_store + row.delivery_service_kaspi_store) + (acc.totalglovo + row.glovo) + (acc.totalvolt + row.volt) + (acc.totalyandex + row.yandex)) || 0,
        totalDifference:  ((acc.totalzreport + row.cashless) - ((acc.totalnarodny_bank + row.narodny_bank) + (acc.totalBereke_bank + row.bereke_bank) + (acc.totalkaspi_red_terminal + row.kaspi_red_terminal) + (acc.totaldelivery_service_kaspi_store + row.delivery_service_kaspi_store) + (acc.totalglovo + row.glovo) + (acc.totalvolt + row.volt) + (acc.totalyandex + row.yandex)) || 0)
      }), {
        totalnarodny_bank: 0,
        totalBereke_bank: 0,
        totalkaspi_red_terminal: 0,
        totaldelivery_service_kaspi_store: 0,
        totalglovo: 0,
        totalvolt: 0,
        totalyandex: 0,
        totalrow: 0,
        totalzreport: 0,
        totalTotal: 0,
        totalDifference: 0,
      });

      const data = {
        pharmacyNum: selectedUser?.pharmacyNum || '',
        currentMonth: getMonthName(startDate), 
        currentYear: getYear(startDate), 
        period: `${startDate} - ${endDate}`,
        employeeName: employeeName || '',
        rows,
        ...totals
      };

      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true
      });

      doc.render(data);

      const out = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      const fileName = `Отчет о поступлении денег по реализации в кредит аптека №${selectedUser?.pharmacyNum} за ${startDate} - ${endDate}.docx`;
      saveAs(out, fileName);
      if (onGenerated) onGenerated();

    } catch (error) {
      console.error('Ошибка при генерации отчета:', error);
      notify.error('Не удалось сформировать отчет по банку');
    }
  };

  return (
    <Button
      variant="outlined"
      onClick={generateReport}
      disabled={disabled || !tableData.length}
      sx={ACTION_BUTTON_SX}
    >
      Банк
    </Button>
  );
};

export default BankReport;